import { useRef } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { useLiveQuery } from 'dexie-react-hooks';
import { AccountingEngine } from '../services/accountingEngine';
import { formatDate } from '../lib/dateUtils';

export const TrialBalanceView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const tbData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    return await AccountingEngine.generateTrialBalance(activeCompany.id!, fromDate, toDate);
  }, [activeCompany, fromDate, toDate]);

  const items = tbData?.items || [];
  
  useWheelNavigation(scrollRef, items.length, setSelectedIndex, false, () => items && items[selectedIndex] && handleItemSelect(items[selectedIndex], selectedIndex));

  if (!tbData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Calculating Trial Balance...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Trial Balance</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config.companyList[0].name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex justify-between border-b border-gray-300 font-bold px-2 py-1 leading-tight items-end">
          <div className="flex flex-col w-1/2">
            <span className="mb-[2px]">Particulars</span>
            <span className="text-[11px] font-normal">{formatDate(fromDate)} to {formatDate(toDate)}</span>
          </div>
          <div className="w-1/2 flex justify-end text-right">
             <div className="flex flex-col w-[120px]">
                <span className="text-center border-b border-gray-300 mb-1">Closing Balance</span>
                <div className="flex justify-between">
                   <span className="w-1/2 text-right pr-2">Debit</span>
                   <span className="w-1/2 text-right">Credit</span>
                </div>
             </div>
          </div>
        </div>
        
        <div ref={scrollRef} className="flex-1 overflow-y-auto pt-1 pb-2">
           {items.map((item: any, idx: number) => {
             const isSelected = selectedIndex === idx;
             return (
               <div key={idx} onClick={() => handleItemSelect(item, idx)} className={`flex justify-between px-2 py-[2px] cursor-pointer ${isSelected ? 'bg-[#1e8585] text-white font-bold active-row' : 'text-black font-bold'}`}>
                  <span className="w-1/2">{item.name}</span>
                  <div className="w-1/2 flex justify-end">
                     <div className="flex w-[120px] justify-between">
                        <span className="w-1/2 text-right pr-2">{item.debit > 0 ? item.debit.toFixed(2) : ''}</span>
                        <span className="w-1/2 text-right">{item.credit > 0 ? item.credit.toFixed(2) : ''}</span>
                     </div>
                  </div>
               </div>
             )
           })}
        </div>
        
        <div className="flex justify-between border-t border-gray-400 font-bold px-2 py-1">
           <span className="w-1/2">Grand Total</span>
           <div className="w-1/2 flex justify-end">
               <div className="flex w-[120px] justify-between">
                  <span className="w-1/2 text-right pr-2">{tbData?.totalDebit.toFixed(2) || '0.00'}</span>
                  <span className="w-1/2 text-right">{tbData?.totalCredit.toFixed(2) || '0.00'}</span>
               </div>
           </div>
        </div>
      </div>
    </div>
  );
};
